import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";

const FinanceDashBoard = () => {
  const today = new Date();
  const currentDate = today.toISOString().split("T")[0];

  const [dashboardDate, setDashboardDate] = useState(currentDate);
  const [showingYesterdayData, setShowingYesterdayData] = useState(false);

  const [openingAmount, setOpeningAmount] = useState(0);
  const [openingProfit, setOpeningProfit] = useState(0);
  const [dailyTotal, setDailyTotal] = useState(0);
  const [weeklyTotal, setWeeklyTotal] = useState(0);
  const [loanDetails, setLoanDetails] = useState([]);
  const [collections, setCollections] = useState([]);
  const [collectionAmount, setCollectionAmount] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);
  const [loanTotal, setLoanTotal] = useState(0);
  const [totalProfit, setTotalProfit] = useState(0);
  const [closingProfit, setClosingProfit] = useState(0);
  const [filteredAmount, setFilteredAmount] = useState([]);
  const baseURL = process.env.REACT_APP_BASE_URL;
  console.log("Display Date:", dashboardDate);

  const formatDisplayDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${String(date.getDate()).padStart(2, "0")}-${String(
      date.getMonth() + 1
    ).padStart(2, "0")}-${date.getFullYear()}`;
  };

  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split("T")[0];
  };

  const formatDateForData = (dateStr) => {
    const [day, month, year] = dateStr.split("-");
    return `${year}-${month}-${day}`;
  };

  const fetchDataForDate = async (dateToUse) => {
    try {
      const accessToken = sessionStorage.getItem("access_token");

      const response = await axios.get(
        `${baseURL}/fetchClosingBalance?date=${dateToUse}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      const data = response.data;
      setOpeningAmount(data.closing_amount);
      setOpeningProfit(data.closing_profit);
      console.log("Fetched Data:", data);

      const loanResp = await axios.get(`${baseURL}/fetchBalance`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setLoanDetails(loanResp.data.loan_details);
      console.log("Loan Details:", loanResp.data);

      const allLoanResp = await axios.get(`${baseURL}/getAllLoan`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setCollections(allLoanResp.data);
      console.log("All Loans:", allLoanResp.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const checkPreviousDaySaved = async () => {
    try {
      const accessToken = sessionStorage.getItem("access_token");
      const yesterday = getYesterdayDate();

      const response = await axios.get(
        `${baseURL}/checkPreviousBalanceSaved?date=${yesterday}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      if (!response.data.exists) {
        setDashboardDate(yesterday);
        setShowingYesterdayData(true);

        Swal.fire({
          icon: "warning",
          title: "Reminder",
          text: `You haven't saved the data for ${formatDisplayDate(
            yesterday
          )}. Showing unsaved data now.`,
          confirmButtonText: "OK",
          customClass: {
            confirmButton:
              "bg-black text-white px-4 py-2 rounded hover:bg-gray-800 focus:outline-none",
          },
          buttonsStyling: false,
        });

        await fetchDataForDate(yesterday);
      } else {
        await fetchDataForDate(currentDate);
      }
    } catch (error) {
      console.error("Error checking previous balance:", error);
    }
  };

  useEffect(() => {
    checkPreviousDaySaved();
  }, []);

  useEffect(() => {
    if (loanDetails.length > 0) {
      const normalizeDate = (dateStr) => {
        if (!dateStr) return ""; // prevent crash if null
        return new Date(dateStr).toISOString().split("T")[0];
      };

      const filtered = loanDetails.filter((loan) => {
        return (
          loan.scheme_name?.toLowerCase() === "daily collection" &&
          normalizeDate(loan.log_date) === dashboardDate
        );
      });

      const filteredWeekly = loanDetails.filter((loan) => {
        return (
          loan.scheme_name?.toLowerCase() === "weekly collection" &&
          normalizeDate(loan.log_date) === dashboardDate
        );
      });

      const totalEmiDailyAmount = filtered.reduce((total, loan) => {
        return total + parseFloat(loan.log_amount?.trim() || 0);
      }, 0);

      const totalEmiWeeklyAmount = filteredWeekly.reduce((total, loan) => {
        return total + parseFloat(loan.log_amount?.trim() || 0);
      }, 0);

      const collectionAmount = totalEmiDailyAmount + totalEmiWeeklyAmount;

      const filteredAmount = collections.filter((loan) => {
        return (
          (loan.scheme_name?.toLowerCase() === "daily collection" ||
            loan.scheme_name?.toLowerCase() === "weekly collection") &&
          normalizeDate(loan.start_date) === dashboardDate
        );
      });

      setFilteredAmount(filteredAmount);
      console.log("Filtered Amount:", filteredAmount);

      const totalAmount = filteredAmount.reduce((total, loan) => {
        return total + parseFloat(loan.amount || 0);
      }, 0);

      const totalProfitAmount = filteredAmount.reduce((total, loan) => {
        return total + parseFloat(loan.initial_profit || 0);
      }, 0);

      setDailyTotal(totalEmiDailyAmount);
      setWeeklyTotal(totalEmiWeeklyAmount);
      setCollectionAmount(collectionAmount);
      setLoanTotal(totalAmount);
      setTotalProfit(totalProfitAmount);

      const calculatedClosingBalance =
        parseFloat(openingAmount) + collectionAmount - totalAmount;
      setClosingBalance(calculatedClosingBalance);

      const calculatedClosingProfit =
        parseFloat(openingProfit) + totalProfitAmount;
      setClosingProfit(calculatedClosingProfit);
    }
  }, [loanDetails, collections, dashboardDate]);

  const handleSave = async () => {
    const dataToSave = {
      currentDate: dashboardDate,
      openingAmount,
      collectionAmount,
      loanTotal,
      closingBalance,
      openingProfit,
      totalProfit,
      closingProfit,
    };

    try {
      const accessToken = sessionStorage.getItem("access_token");
      await axios.post(`${baseURL}/insertBalance`, dataToSave, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      Swal.fire({
        icon: "success",
        text: "Data inserted successfully!",
        timer: 2000,
        showConfirmButton: false,
      });

      if (showingYesterdayData) {
        setDashboardDate(currentDate);
        setShowingYesterdayData(false);
        await fetchDataForDate(currentDate);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        text:
          error.response?.data?.error ||
          "An error occurred while saving the data.",
        timer: 2000,
        showConfirmButton: false,
      });

      console.error("Error saving data:", error);
    }
  };

  return (
    <div className="p-6 bg-white shadow-lg rounded-lg max-w-4xl mx-auto mt-10 overflow-y-auto h-[calc(100vh-4rem)]">
      {showingYesterdayData && (
        <div className="bg-yellow-200 text-yellow-800 p-3 rounded text-center font-semibold mb-4">
          ⚠️ Showing Unsaved Data for {formatDisplayDate(dashboardDate)}{" "}
          (Yesterday)
        </div>
      )}

      <h2 className="text-3xl font-bold text-center mb-6">
        Date : {formatDisplayDate(dashboardDate)}
      </h2>
      <div className="grid grid-cols-2 gap-8">
        {/* Investment Table */}
        <div>
          <h3 className="text-xl font-bold mb-4 text-center">Investment</h3>
          <table className="w-full border">
            <tbody>
              <tr className="bg-gray-100">
                <td className="p-2 border font-bold text-left">
                  Opening Balance
                </td>
                <td className="p-2 border font-bold text-right">
                  {openingAmount}
                </td>
              </tr>
              <tr>
                <td className="p-2 border text-right">Daily</td>
                <td className="p-2 border text-right">{dailyTotal}</td>
              </tr>
              <tr className="bg-gray-100 text-right">
                <td className="p-2 border">Weekly</td>
                <td className="p-2 border text-right">{weeklyTotal}</td>
              </tr>
              <tr>
                <td className="p-2 border font-bold text-left">
                  Collection Amount (+)
                </td>
                <td className="p-2 border text-right font-bold">
                  {collectionAmount}
                </td>
              </tr>
              {filteredAmount.map((loan, index) => (
                <tr
                  className={index % 2 === 0 ? "bg-gray-100" : ""}
                  key={index}
                >
                  <td className="p-2 border text-right">{loan.tamilname}</td>
                  <td className="p-2 border text-right">{loan.amount}</td>
                </tr>
              ))}
              <tr className="bg-gray-100">
                <td className="p-2 border font-bold text-left">
                  New Loan Amount (-)
                </td>
                <td className="p-2 border  font-bold text-right">
                  {loanTotal}
                </td>
              </tr>
              <tr>
                <td className="p-2 border font-bold text-left">
                  Closing Balance
                </td>
                <td className="p-2 border  font-bold text-right">
                  {closingBalance}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Profit Table */}
        <div>
          <h3 className="text-xl font-bold mb-4 text-center">Profit</h3>
          <table className="w-full border">
            <tbody>
              <tr className="bg-gray-100">
                <td className="p-2 border font-bold text-left">
                  Opening Profit
                </td>
                <td className="p-2 border text-right font-bold">
                  {openingProfit}
                </td>
              </tr>
              {filteredAmount.map((loan, index) => (
                <tr key={index}>
                  <td
                    className={`p-2 border text-right ${
                      index % 2 != 0 ? "bg-gray-100" : ""
                    }`}
                  >
                    {loan.tamilname}
                  </td>
                  <td
                    className={`p-2 border text-right ${
                      index % 2 != 0 ? "bg-gray-100" : ""
                    }`}
                  >
                    {loan.initial_profit}
                  </td>
                </tr>
              ))}
              <tr>
                <td className="p-2 font-bold border">Profit</td>
                <td className="p-2 border text-right font-bold">
                  {totalProfit}
                </td>
              </tr>
              <tr className="bg-gray-100">
                <td className="p-2 border font-bold text-left">
                  Closing Profit
                </td>
                <td className="p-2 border text-right font-bold">
                  {closingProfit}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex justify-center mt-8">
        <button
          onClick={handleSave}
          className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600"
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default FinanceDashBoard;
